import { getServiceFloor } from '@/apis/cart/cart.api';
import type { LocalInfoType } from '@/apis/common.types';
import NotAvailableIcon from '@/assets/icons/noAvailableService.svg';
import type { ServiceFloorData } from '@/components/product/interface';
import UKDrawer from '@/components/UKDrawer';
import UKPageLoading from '@/components/UKPageLoading';
import { useTranslation } from '@/i18n/client';
import { useStores } from '@/models';
import to from 'await-to-js';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { AddCartButton, BuyNowButton } from '../buttons';
import { BUTTON_TYPE } from '../const';
import ServiceList from '../serviceList';
import style from './drawer.module.scss';

interface DialogProps {
  open: boolean; //打开状态
  title?: string; //标题
  mainSkuId: string; //商品SkuId
  serviceData?: ServiceFloorData; //服务数据
  btnType: BUTTON_TYPE; //底部展示按钮类型
  useShareCheck?: boolean; //服务选择态是否同步缓存
  closeFn: () => void; //关闭回调
  addCart?: (param: {}) => void; //加车点击回调
  buyNow?: (param: {}) => void; //立即购买点击回调
  onConfirm?: (param: {}) => void; //确认按钮点击回调
  isInitialized?: boolean; //是否请求服务数据
  localInfo?: LocalInfoType; //货币信息
  serviceItemIds?: string[]; // 选择的服务ID列表
}

const ServiceGuaranteeDrawer = ({
  open,
  title,
  mainSkuId,
  serviceData,
  btnType,
  closeFn,
  addCart,
  buyNow,
  onConfirm,
  useShareCheck = false,
  isInitialized = true,
  localInfo,
  serviceItemIds,
}: DialogProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  // 请求服务数据时的状态
  const [requestData, setRequestData] = useState({
    ServiceFloorData: serviceData,
    localInfo: localInfo,
  });
  const [isLoading, setIsLoading] = useState(false);
  const { serviceListStore } = useStores();
  const { getServiceParam } = serviceListStore;

  const handleClose = useCallback(() => {
    setIsOpen(false);
    closeFn?.();
  }, [closeFn]);

  const onAddCart = () => {
    handleClose();
    addCart?.(getServiceParam?.());
  };

  const onBuyNow = () => {
    handleClose();
    buyNow?.(getServiceParam?.());
  };

  const handleConfirm = () => {
    handleClose();
    onConfirm?.(getServiceParam?.());
  };

  const requestServiceData = useCallback(async () => {
    setIsLoading(true);
    const [err, response] = await to(
      getServiceFloor({
        floorId: 'productServiceItemsFloor',
        skuId: mainSkuId,
        scene: '1',
        serviceItemIds: serviceItemIds || [],
      }),
    );
    setIsLoading(false);
    console.log('requestServiceData response', response);
    setRequestData({
      ServiceFloorData: response?.data || {},
      localInfo: response?.localInfo || {},
    });
  }, [mainSkuId, serviceItemIds]);

  function serviceFloorDocm() {
    if (requestData && requestData.ServiceFloorData) {
      if (!!requestData.ServiceFloorData.serviceItems?.length && requestData.localInfo) {
        return (
          <ServiceList
            serviceData={requestData.ServiceFloorData}
            useShareCheck={useShareCheck}
            mainSkuId={mainSkuId}
            localInfo={requestData.localInfo}
            isDoubleCheck={true}
          />
        );
      }
      if (!requestData.ServiceFloorData.serviceItems?.length) {
        return (
          <div className={style.not_available}>
            <NotAvailableIcon />
            <div>{t('cart.noAvailableService')}</div>
          </div>
        );
      }
    }
  }

  // 如需要，打开抽屉时请求服务数据
  useEffect(() => {
    setIsOpen(open);
    if (!isInitialized && open) {
      requestServiceData();
    }
  }, [open, isInitialized, requestServiceData]);

  return (
    <UKDrawer
      width={430}
      title={!isInitialized ? requestData.ServiceFloorData?.title : title}
      openDrawer={isOpen}
      handleClose={handleClose}
    >
      {isLoading ? (
        <UKPageLoading loading={isLoading} text={t('common.pageStatus.loading')} />
      ) : (
        <div className={style.drawer}>
          <div className={style.container}>{serviceFloorDocm()}</div>
          {btnType === BUTTON_TYPE.AddCart && (
            <AddCartButton sx={{ width: '100%' }} size='large' onClick={onAddCart}>
              {t('dp.addToCart')}
            </AddCartButton>
          )}
          {btnType === BUTTON_TYPE.BuyNow && (
            <BuyNowButton sx={{ width: '100%' }} size='large' onClick={onBuyNow}>
              {t('dp.buyNow')}
            </BuyNowButton>
          )}
          {btnType === BUTTON_TYPE.Confirm && (
            <BuyNowButton sx={{ width: '100%' }} size='large' onClick={handleConfirm}>
              {t('cart.update')}
            </BuyNowButton>
          )}
        </div>
      )}
    </UKDrawer>
  );
};

export default observer(ServiceGuaranteeDrawer);
